/**
 * Staff Management Service
 *
 * Handles staff hierarchy management including:
 * - Staff designations (top-down: supervisor designates staff)
 * - Join requests (bottom-up: staff requests to join)
 * - Domain restrictions for facility email validation
 * - Multi-facility user support
 *
 * Error handling convention: catch blocks log with console.error('[ServiceName] functionName:', error)
 * then either rethrow or return a safe value (e.g. [], null). Non-fatal cases are commented.
 */

import { FACILITY_TYPE } from '@healthdesk/shared-utils';
import { db } from './firebase';
import firebase from 'firebase/compat/app';

// ============================================================================
// CONSTANTS
// ============================================================================

export const DESIGNATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
  REVOKED: 'revoked',
};

export const JOIN_REQUEST_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  DENIED: 'denied',
};

export const DESIGNATABLE_ROLES = {
  'facility': ['chartmind-admin', 'chartmind-kijabe', 'department-admin', 'chartmind-supervisor', 'professional-chartmind', 'pro2'],
  'department-admin': ['chartmind-supervisor', 'professional-chartmind', 'pro2'],
  'chartmind-supervisor': ['professional-chartmind', 'pro2'],
};

// Default expiration: 30 days
const DESIGNATION_EXPIRY_DAYS = 30;

// ============================================================================
// STAFF DESIGNATION FUNCTIONS
// ============================================================================

/**
 * Create a new staff designation
 * @param {string} designatorUid - UID of the user creating the designation
 * @param {Object} designationData - Designation details
 * @returns {Promise<string>} Designation ID
 */
export async function createDesignation(designatorUid, designationData) {
  if (!designatorUid) {
    throw new Error('Designator UID is required');
  }
  
  const {
    email,
    phone,
    name,
    facilityId,
    departmentId,
    clinicId,
    designatedRole,
  } = designationData;
  
  if (!email) {
    throw new Error('Email is required for designation');
  }
  
  if (!facilityId) {
    throw new Error('Facility ID is required for designation');
  }
  
  if (!designatedRole) {
    throw new Error('Designated role is required');
  }
  
  // Validate domain restriction if enabled
  const domainValid = await validateDomainRestriction(email, facilityId);
  if (!domainValid.valid) {
    throw new Error(domainValid.error);
  }
  
  // Check for existing pending designation
  const existingPending = await db
    .collection('staffDesignations')
    .where('email', '==', email.toLowerCase())
    .where('facilityId', '==', facilityId)
    .where('status', '==', DESIGNATION_STATUS.PENDING)
    .get();
  
  if (!existingPending.empty) {
    throw new Error('A pending designation already exists for this email at this facility');
  }
  
  const now = firebase.firestore.Timestamp.now();
  const expiresAt = firebase.firestore.Timestamp.fromDate(
    new Date(Date.now() + DESIGNATION_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
  );
  
  const designation = {
    email: email.toLowerCase(),
    phone: phone || null,
    name: name || '',
    facilityId,
    departmentId: departmentId || null,
    clinicId: clinicId || null,
    designatedRole,
    status: DESIGNATION_STATUS.PENDING,
    createdBy: designatorUid,
    createdAt: now,
    expiresAt,
    acceptedAt: null,
    acceptedByUid: null,
    notificationsSent: {
      email: false,
      sms: false,
      inApp: false,
    },
  };
  
  const docRef = await db.collection('staffDesignations').add(designation);
  
  return docRef.id;
}

/**
 * Get all designations for a facility
 * @param {string} facilityId - Facility ID
 * @param {Object} filters - Optional filters (status, departmentId, clinicId)
 * @returns {Promise<Array>} Array of designations
 */
export async function getDesignationsForFacility(facilityId, filters = {}) {
  if (!facilityId) {
    console.warn('[StaffManagement] No facilityId provided');
    return [];
  }
  
  try {
    let query = db
      .collection('staffDesignations')
      .where('facilityId', '==', facilityId);
    
    if (filters.status) {
      query = query.where('status', '==', filters.status);
    }
    
    if (filters.departmentId) {
      query = query.where('departmentId', '==', filters.departmentId);
    }
    
    if (filters.clinicId) {
      query = query.where('clinicId', '==', filters.clinicId);
    }
    
    const snapshot = await query.orderBy('createdAt', 'desc').get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffManagement] getDesignations:', error);
    return [];
  }
}

/**
 * Get designations created by a specific user
 * @param {string} uid - User ID
 * @returns {Promise<Array>} Array of designations
 */
export async function getDesignationsCreatedBy(uid) {
  if (!uid) {
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('staffDesignations')
      .where('createdBy', '==', uid)
      .orderBy('createdAt', 'desc')
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffManagement] getDesignationsCreatedBy:', error);
    return [];
  }
}

/**
 * Get pending designations for an email address
 * @param {string} email - Email address
 * @returns {Promise<Array>} Array of pending designations
 */
export async function getPendingDesignationsForEmail(email) {
  if (!email) {
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('staffDesignations')
      .where('email', '==', email.toLowerCase())
      .where('status', '==', DESIGNATION_STATUS.PENDING)
      .get();
    
    // Filter out expired designations
    const now = new Date();
    return snapshot.docs
      .map(
        (doc) =>
          /** @type {Record<string, any> & { id: string }} */ ({
            id: doc.id,
            ...doc.data(),
          })
      )
      .filter((d) => d.expiresAt && d.expiresAt.toDate() > now);
  } catch (error) {
    console.error('[StaffManagement] getPendingDesignationsForEmail:', error);
    return [];
  }
}

/**
 * Get a single designation by ID
 * @param {string} designationId - Designation ID
 * @returns {Promise<Object|null>} Designation data or null
 */
export async function getDesignation(designationId) {
  if (!designationId) {
    return null;
  }
  
  try {
    const doc = await db.collection('staffDesignations').doc(designationId).get();
    
    if (!doc.exists) {
      return null;
    }
    
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('[StaffManagement] getDesignation:', error);
    return null;
  }
}

/**
 * Accept a designation and update user's hierarchy
 * @param {string} designationId - Designation ID
 * @param {string} acceptingUid - UID of the accepting user
 * @returns {Promise<void>}
 */
export async function acceptDesignation(designationId, acceptingUid) {
  if (!designationId || !acceptingUid) {
    throw new Error('Designation ID and accepting user ID are required');
  }
  
  const designation = await getDesignation(designationId);
  
  if (!designation) {
    throw new Error('Designation not found');
  }
  
  if (designation.status !== DESIGNATION_STATUS.PENDING) {
    throw new Error(`Designation cannot be accepted: status is ${designation.status}`);
  }
  
  if (designation.expiresAt.toDate() < new Date()) {
    throw new Error('Designation has expired');
  }
  
  // Verify accepting user's email matches
  const userDoc = await db.collection('users').doc(acceptingUid).get();
  if (!userDoc.exists) {
    throw new Error('User not found');
  }
  
  const userData = userDoc.data();
  if (userData.email?.toLowerCase() !== designation.email) {
    throw new Error('User email does not match designation email');
  }

  // Validate invitee email domain at accept time (per plan: TLD restriction)
  const domainValid = await validateDomainRestriction(userData.email, designation.facilityId);
  if (!domainValid.valid) {
    throw new Error(domainValid.error || 'This invite is only valid for certain email domains.');
  }
  
  const batch = db.batch();
  
  // Update designation status
  const designationRef = db.collection('staffDesignations').doc(designationId);
  batch.update(designationRef, {
    status: DESIGNATION_STATUS.ACCEPTED,
    acceptedAt: firebase.firestore.Timestamp.now(),
    acceptedByUid: acceptingUid,
  });
  
  // Update user's hierarchy - add to facilities array for multi-facility support
  const userRef = db.collection('users').doc(acceptingUid);
  const facilityAssignment = {
    facilityId: designation.facilityId,
    departmentId: designation.departmentId,
    clinicId: designation.clinicId,
    role: designation.designatedRole,
    assignedAt: firebase.firestore.Timestamp.now(),
    assignedBy: designation.createdBy,
  };
  
  // Get current facilities array or create new one
  const currentFacilities = userData.facilities || [];
  
  // Check if already assigned to this specific facility+clinic combination
  // This allows multi-clinic assignments within the same facility
  const existingIndex = currentFacilities.findIndex(
    f => f.facilityId === designation.facilityId && f.clinicId === designation.clinicId
  );
  
  if (existingIndex >= 0) {
    // Update existing facility+clinic assignment
    currentFacilities[existingIndex] = facilityAssignment;
  } else {
    // Add new facility+clinic assignment
    currentFacilities.push(facilityAssignment);
  }
  
  batch.update(userRef, {
    facilities: currentFacilities,
    // Set as primary if this is the first facility
    ...(currentFacilities.length === 1 && {
      primaryFacilityId: designation.facilityId,
      facilityId: designation.facilityId,
      departmentId: designation.departmentId,
      clinicId: designation.clinicId,
      role: designation.designatedRole,
    }),
  });
  
  await batch.commit();
  
}

/**
 * Decline a designation
 * @param {string} designationId - Designation ID
 * @param {string} reason - Optional reason for declining
 * @returns {Promise<void>}
 */
export async function declineDesignation(designationId, reason = '') {
  if (!designationId) {
    throw new Error('Designation ID is required');
  }
  
  const designation = await getDesignation(designationId);
  
  if (!designation) {
    throw new Error('Designation not found');
  }
  
  if (designation.status !== DESIGNATION_STATUS.PENDING) {
    throw new Error(`Designation cannot be declined: status is ${designation.status}`);
  }
  
  await db.collection('staffDesignations').doc(designationId).update({
    status: DESIGNATION_STATUS.DECLINED,
    declinedAt: firebase.firestore.Timestamp.now(),
    declineReason: reason,
  });
  
}

/**
 * Revoke a designation (supervisor action)
 * @param {string} designationId - Designation ID
 * @param {string} revokedBy - UID of the user revoking
 * @returns {Promise<void>}
 */
export async function revokeDesignation(designationId, revokedBy) {
  if (!designationId || !revokedBy) {
    throw new Error('Designation ID and revoking user ID are required');
  }
  
  const designation = await getDesignation(designationId);
  
  if (!designation) {
    throw new Error('Designation not found');
  }
  
  // Can only revoke pending or accepted designations
  if (![DESIGNATION_STATUS.PENDING, DESIGNATION_STATUS.ACCEPTED].includes(designation.status)) {
    throw new Error(`Designation cannot be revoked: status is ${designation.status}`);
  }
  
  await db.collection('staffDesignations').doc(designationId).update({
    status: DESIGNATION_STATUS.REVOKED,
    revokedAt: firebase.firestore.Timestamp.now(),
    revokedBy,
  });
  
  // If designation was accepted, remove user from specific clinic
  if (designation.status === DESIGNATION_STATUS.ACCEPTED && designation.acceptedByUid) {
    await removeUserFromFacility(designation.acceptedByUid, designation.facilityId, designation.clinicId);
  }
  
}

/**
 * Remove a user from a specific clinic within a facility
 * @param {string} uid - User ID
 * @param {string} facilityId - Facility ID
 * @param {string} clinicId - Clinic ID (optional - if not provided, removes all assignments for the facility)
 * @returns {Promise<void>}
 */
export async function removeUserFromFacility(uid, facilityId, clinicId = null) {
  const userDoc = await db.collection('users').doc(uid).get();
  
  if (!userDoc.exists) {
    return;
  }
  
  const userData = userDoc.data();
  const facilities = userData.facilities || [];
  
  // Remove the specific clinic assignment, or all assignments for the facility
  const updatedFacilities = clinicId
    ? facilities.filter(f => !(f.facilityId === facilityId && f.clinicId === clinicId))
    : facilities.filter(f => f.facilityId !== facilityId);
  
  const updates = {
    facilities: updatedFacilities,
  };
  
  // Check if we need to update primary assignment
  const currentPrimaryClinicId = userData.clinicId;
  const removedCurrentPrimary = clinicId
    ? (userData.primaryFacilityId === facilityId && currentPrimaryClinicId === clinicId)
    : (userData.primaryFacilityId === facilityId);
  
  if (removedCurrentPrimary) {
    if (updatedFacilities.length > 0) {
      const newPrimary = updatedFacilities[0];
      updates.primaryFacilityId = newPrimary.facilityId;
      updates.facilityId = newPrimary.facilityId;
      updates.departmentId = newPrimary.departmentId;
      updates.clinicId = newPrimary.clinicId;
      updates.role = newPrimary.role;
    } else {
      updates.primaryFacilityId = null;
      updates.facilityId = null;
      updates.departmentId = null;
      updates.clinicId = null;
      // Keep role as professional-chartmind for orphaned users
      updates.role = 'professional-chartmind';
    }
  }
  
  await db.collection('users').doc(uid).update(updates);
  
}

// ============================================================================
// JOIN REQUEST FUNCTIONS
// ============================================================================

/**
 * Create a join request (user requesting to join a facility)
 * @param {string} requesterUid - UID of the requesting user
 * @param {Object} requestData - Request details
 * @returns {Promise<string>} Request ID
 */
export async function createJoinRequest(requesterUid, requestData) {
  if (!requesterUid) {
    throw new Error('Requester UID is required');
  }
  
  const {
    facilityId,
    departmentId,
    clinicId,
    requestedRole,
  } = requestData;
  
  if (!facilityId) {
    throw new Error('Facility ID is required');
  }
  
  if (!requestedRole) {
    throw new Error('Requested role is required');
  }
  
  // Get requester info
  const userDoc = await db.collection('users').doc(requesterUid).get();
  if (!userDoc.exists) {
    throw new Error('User not found');
  }
  
  const userData = userDoc.data();

  // Validate invitee email domain at request time (per plan: TLD restriction)
  const domainValid = await validateDomainRestriction(userData.email, facilityId);
  if (!domainValid.valid) {
    throw new Error(domainValid.error || 'This facility only accepts join requests from certain email domains.');
  }
  
  // Check for existing pending request
  const existingPending = await db
    .collection('joinRequests')
    .where('requesterUid', '==', requesterUid)
    .where('facilityId', '==', facilityId)
    .where('status', '==', JOIN_REQUEST_STATUS.PENDING)
    .get();
  
  if (!existingPending.empty) {
    throw new Error('You already have a pending request for this facility');
  }
  
  const request = {
    requesterUid,
    requesterEmail: userData.email?.toLowerCase() || '',
    requesterName: `${userData.name || ''} ${userData.lastName || ''}`.trim(),
    facilityId,
    departmentId: departmentId || null,
    clinicId: clinicId || null,
    requestedRole,
    status: JOIN_REQUEST_STATUS.PENDING,
    reviewedBy: null,
    reviewedAt: null,
    denialReason: null,
    createdAt: firebase.firestore.Timestamp.now(),
  };
  
  const docRef = await db.collection('joinRequests').add(request);
  
  return docRef.id;
}

/**
 * Get pending join requests for a supervisor
 * @param {string} supervisorUid - Supervisor's UID
 * @param {Object} userScope - User's scope (facilityId, departmentId, clinicId, role)
 * @returns {Promise<Array>} Array of join requests
 */
export async function getJoinRequestsForSupervisor(supervisorUid, userScope) {
  if (!supervisorUid || !userScope?.facilityId) {
    return [];
  }
  
  try {
    let query = db
      .collection('joinRequests')
      .where('facilityId', '==', userScope.facilityId)
      .where('status', '==', JOIN_REQUEST_STATUS.PENDING);
    
    // Scope by department for department admins
    if (userScope.role === 'department-admin' && userScope.departmentId) {
      query = query.where('departmentId', '==', userScope.departmentId);
    }
    
    // Scope by clinic for clinic supervisors
    if (userScope.role === 'chartmind-supervisor' && userScope.clinicId) {
      query = query.where('clinicId', '==', userScope.clinicId);
    }
    
    const snapshot = await query.orderBy('createdAt', 'desc').get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffManagement] getJoinRequests:', error);
    return [];
  }
}

/**
 * Get join requests created by a user
 * @param {string} uid - User ID
 * @returns {Promise<Array>} Array of join requests
 */
export async function getJoinRequestsByUser(uid) {
  if (!uid) {
    return [];
  }
  
  try {
    const snapshot = await db
      .collection('joinRequests')
      .where('requesterUid', '==', uid)
      .orderBy('createdAt', 'desc')
      .get();
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('[StaffManagement] getUserJoinRequests:', error);
    return [];
  }
}

/**
 * Get a single join request by ID
 * @param {string} requestId - Request ID
 * @returns {Promise<Object|null>} Request data or null
 */
export async function getJoinRequest(requestId) {
  if (!requestId) {
    return null;
  }
  
  try {
    const doc = await db.collection('joinRequests').doc(requestId).get();
    
    if (!doc.exists) {
      return null;
    }
    
    return {
      id: doc.id,
      ...doc.data(),
    };
  } catch (error) {
    console.error('[StaffManagement] getJoinRequest:', error);
    return null;
  }
}

/**
 * Approve a join request
 * @param {string} requestId - Request ID
 * @param {string} approverUid - UID of the approving supervisor
 * @returns {Promise<void>}
 */
export async function approveJoinRequest(requestId, approverUid) {
  if (!requestId || !approverUid) {
    throw new Error('Request ID and approver ID are required');
  }
  
  const request = await getJoinRequest(requestId);
  
  if (!request) {
    throw new Error('Join request not found');
  }
  
  if (request.status !== JOIN_REQUEST_STATUS.PENDING) {
    throw new Error(`Request cannot be approved: status is ${request.status}`);
  }
  
  const batch = db.batch();
  
  // Update request status
  const requestRef = db.collection('joinRequests').doc(requestId);
  batch.update(requestRef, {
    status: JOIN_REQUEST_STATUS.APPROVED,
    reviewedBy: approverUid,
    reviewedAt: firebase.firestore.Timestamp.now(),
  });
  
  // Update user's hierarchy
  const userRef = db.collection('users').doc(request.requesterUid);
  const userDoc = await userRef.get();
  
  if (!userDoc.exists) {
    throw new Error('Requester user not found');
  }
  
  const userData = userDoc.data();
  const facilityAssignment = {
    facilityId: request.facilityId,
    departmentId: request.departmentId,
    clinicId: request.clinicId,
    role: request.requestedRole,
    assignedAt: firebase.firestore.Timestamp.now(),
    assignedBy: approverUid,
  };
  
  const currentFacilities = userData.facilities || [];
  
  // Check if already assigned to this facility
  const existingIndex = currentFacilities.findIndex(
    f => f.facilityId === request.facilityId
  );
  
  if (existingIndex >= 0) {
    currentFacilities[existingIndex] = facilityAssignment;
  } else {
    currentFacilities.push(facilityAssignment);
  }
  
  batch.update(userRef, {
    facilities: currentFacilities,
    ...(currentFacilities.length === 1 && {
      primaryFacilityId: request.facilityId,
      facilityId: request.facilityId,
      departmentId: request.departmentId,
      clinicId: request.clinicId,
      role: request.requestedRole,
    }),
  });
  
  await batch.commit();
  
}

/**
 * Deny a join request
 * @param {string} requestId - Request ID
 * @param {string} denierUid - UID of the denying supervisor
 * @param {string} reason - Reason for denial
 * @returns {Promise<void>}
 */
export async function denyJoinRequest(requestId, denierUid, reason = '') {
  if (!requestId || !denierUid) {
    throw new Error('Request ID and denier ID are required');
  }
  
  const request = await getJoinRequest(requestId);
  
  if (!request) {
    throw new Error('Join request not found');
  }
  
  if (request.status !== JOIN_REQUEST_STATUS.PENDING) {
    throw new Error(`Request cannot be denied: status is ${request.status}`);
  }
  
  await db.collection('joinRequests').doc(requestId).update({
    status: JOIN_REQUEST_STATUS.DENIED,
    reviewedBy: denierUid,
    reviewedAt: firebase.firestore.Timestamp.now(),
    denialReason: reason,
  });
  
}

// ============================================================================
// DOMAIN RESTRICTION FUNCTIONS
// ============================================================================

/**
 * Get facility settings including domain restrictions
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Facility settings
 */
export async function getFacilitySettings(facilityId) {
  if (!facilityId) {
    return {
      domainRestrictionEnabled: false,
      allowedDomains: [],
    };
  }
  
  try {
    const doc = await db
      .collection('facilities')
      .doc(facilityId)
      .collection('settings')
      .doc('general')
      .get();
    
    if (!doc.exists) {
      return {
        domainRestrictionEnabled: false,
        allowedDomains: [],
      };
    }
    
    return doc.data();
  } catch (error) {
    console.error('[StaffManagement] getFacilitySettings:', error);
    return {
      domainRestrictionEnabled: false,
      allowedDomains: [],
    };
  }
}

/**
 * Validate an email against facility domain restrictions
 * @param {string} email - Email to validate
 * @param {string} facilityId - Facility ID
 * @returns {Promise<{valid: boolean, error?: string}>} Validation result
 */
export async function validateDomainRestriction(email, facilityId) {
  if (!email || !facilityId) {
    return { valid: true };
  }
  
  const settings = await getFacilitySettings(facilityId);
  
  if (!settings.domainRestrictionEnabled || !settings.allowedDomains?.length) {
    return { valid: true };
  }
  
  const emailDomain = email.toLowerCase().split('@')[1];
  
  if (!emailDomain) {
    return { valid: false, error: 'Invalid email format' };
  }
  
  const isAllowed = settings.allowedDomains.some(
    domain => domain.toLowerCase() === emailDomain
  );
  
  if (!isAllowed) {
    return {
      valid: false,
      error: `Email domain must be one of: ${settings.allowedDomains.join(', ')}`,
    };
  }
  
  return { valid: true };
}

/**
 * Update facility domain settings
 * @param {string} facilityId - Facility ID
 * @param {Object} settings - Domain settings
 * @returns {Promise<void>}
 */
export async function updateFacilityDomainSettings(facilityId, settings) {
  if (!facilityId) {
    throw new Error('Facility ID is required');
  }
  
  const { domainRestrictionEnabled, allowedDomains } = settings;
  
  await db
    .collection('facilities')
    .doc(facilityId)
    .collection('settings')
    .doc('general')
    .set({
      domainRestrictionEnabled: Boolean(domainRestrictionEnabled),
      allowedDomains: (allowedDomains || []).map(d => d.toLowerCase().trim()),
      updatedAt: firebase.firestore.Timestamp.now(),
    }, { merge: true });
  
}

// ============================================================================
// FACILITY SEARCH FUNCTIONS (for join request flow)
// ============================================================================

/**
 * Search facilities by name or code
 * @param {string} searchTerm - Search term
 * @returns {Promise<Array>} Array of matching facilities
 */
export async function searchFacilities(searchTerm) {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }
  
  try {
    // Search in locations collection (CareMap entries)
    const locationsSnapshot = await db
      .collection('locations')
      .where('type', '==', FACILITY_TYPE.CLINIC)
      .limit(50)
      .get();
    
    const searchLower = searchTerm.toLowerCase();
    
    const results = locationsSnapshot.docs
      .map(
        (doc) =>
          /** @type {Record<string, any> & { id: string }} */ ({
            id: doc.id,
            ...doc.data(),
          })
      )
      .filter(
        (loc) =>
          loc.name?.toLowerCase().includes(searchLower) ||
          loc.uniqueSignupCode?.toLowerCase() === searchLower
      )
      .slice(0, 10);
    
    return results;
  } catch (error) {
    console.error('[StaffManagement] searchFacilities:', error);
    return [];
  }
}

/**
 * Get facility details including departments and clinics
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object|null>} Facility details or null
 */
export async function getFacilityDetails(facilityId) {
  if (!facilityId) {
    return null;
  }
  
  try {
    // Get facility/location info
    const locationDoc = await db.collection('locations').doc(facilityId).get();
    
    // Get departments
    const departmentsSnapshot = await db
      .collection('clinics')
      .doc(facilityId)
      .collection('departments')
      .get();
    
    // Get clinics (check-in templates)
    const clinicsSnapshot = await db
      .collection('clinics')
      .doc(facilityId)
      .collection('checkInTemplates')
      .get();
    
    return {
      id: facilityId,
      ...(locationDoc.exists ? locationDoc.data() : {}),
      departments: departmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })),
      clinics: clinicsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })),
    };
  } catch (error) {
    console.error('[StaffManagement] getFacilityDetails:', error);
    return null;
  }
}

// ============================================================================
// STAFF LISTING FUNCTIONS
// ============================================================================

/**
 * Get all staff members for a facility/department/clinic
 * @param {string} facilityId - Facility ID
 * @param {Object} filters - Optional filters (departmentId, clinicId, role)
 * @returns {Promise<Array>} Array of staff users
 */
export async function getStaffMembers(facilityId, filters = {}) {
  if (!facilityId) {
    return [];
  }
  
  try {
    // Query users who have this facility in their facilities array
    const usersSnapshot = await db
      .collection('users')
      .where('facilities', 'array-contains-any', [
        { facilityId },
      ])
      .limit(100)
      .get();
    
    // Since Firestore doesn't support complex array queries well,
    // we need to filter in-memory
    let staff = [];
    
    // Alternative: Query by facilityId field for backwards compatibility
    const facilityUsersSnapshot = await db
      .collection('users')
      .where('facilityId', '==', facilityId)
      .get();
    
    facilityUsersSnapshot.docs.forEach(doc => {
      const userData = doc.data();
      
      // Apply department filter
      if (filters.departmentId && userData.departmentId !== filters.departmentId) {
        return;
      }
      
      // Apply clinic filter
      if (filters.clinicId && userData.clinicId !== filters.clinicId) {
        return;
      }
      
      // Apply role filter
      if (filters.role && userData.role !== filters.role) {
        return;
      }
      
      staff.push({
        id: doc.id,
        uid: doc.id,
        name: userData.name || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        role: userData.role,
        departmentId: userData.departmentId,
        clinicId: userData.clinicId,
      });
    });
    
    return staff;
  } catch (error) {
    console.error('[StaffManagement] getStaffMembers:', error);
    return [];
  }
}

/**
 * Get roles that a supervisor can designate based on their role
 * @param {string} supervisorRole - Supervisor's role
 * @returns {Array<string>} Array of designatable roles
 */
export function getDesignatableRoles(supervisorRole) {
  return DESIGNATABLE_ROLES[supervisorRole] || [];
}

// ============================================================================
// MULTI-FACILITY FUNCTIONS
// ============================================================================

/**
 * Switch user's active facility
 * @param {string} uid - User ID
 * @param {string} facilityId - Facility ID to switch to
 * @returns {Promise<void>}
 */
export async function switchActiveFacility(uid, facilityId) {
  if (!uid || !facilityId) {
    throw new Error('User ID and facility ID are required');
  }
  
  const userDoc = await db.collection('users').doc(uid).get();
  
  if (!userDoc.exists) {
    throw new Error('User not found');
  }
  
  const userData = userDoc.data();
  const facilities = userData.facilities || [];
  
  const targetFacility = facilities.find(f => f.facilityId === facilityId);
  
  if (!targetFacility) {
    throw new Error('User is not assigned to this facility');
  }
  
  await db.collection('users').doc(uid).update({
    primaryFacilityId: facilityId,
    facilityId: facilityId,
    departmentId: targetFacility.departmentId,
    clinicId: targetFacility.clinicId,
    role: targetFacility.role,
  });
  
}

/**
 * Get user's facility assignments
 * @param {string} uid - User ID
 * @returns {Promise<Array>} Array of facility assignments
 */
export async function getUserFacilities(uid) {
  if (!uid) {
    return [];
  }
  
  try {
    const userDoc = await db.collection('users').doc(uid).get();
    
    if (!userDoc.exists) {
      return [];
    }
    
    const userData = userDoc.data();
    return userData.facilities || [];
  } catch (error) {
    console.error('[StaffManagement] getUserFacilities:', error);
    return [];
  }
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Constants
  DESIGNATION_STATUS,
  JOIN_REQUEST_STATUS,
  DESIGNATABLE_ROLES,
  
  // Designation functions
  createDesignation,
  getDesignationsForFacility,
  getDesignationsCreatedBy,
  getPendingDesignationsForEmail,
  getDesignation,
  acceptDesignation,
  declineDesignation,
  revokeDesignation,
  removeUserFromFacility,
  
  // Join request functions
  createJoinRequest,
  getJoinRequestsForSupervisor,
  getJoinRequestsByUser,
  getJoinRequest,
  approveJoinRequest,
  denyJoinRequest,
  
  // Domain restriction functions
  getFacilitySettings,
  validateDomainRestriction,
  updateFacilityDomainSettings,
  
  // Facility search functions
  searchFacilities,
  getFacilityDetails,
  
  // Staff listing functions
  getStaffMembers,
  getDesignatableRoles,
  
  // Multi-facility functions
  switchActiveFacility,
  getUserFacilities,
};
